import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { backend_url } from "../App";
import { ABOUT_FABRIC_DEFAULTS as D, ABOUT_FABRIC_LIMITS as L } from "../Constants/aboutFabricDefaults";
import aboutGif from "../Components/Assets/about-fabric.gif";
import "./CSS/AboutFabricEditor.css";
import SaveBar from "../Components/SaveBar/SaveBar";

const KEY = "about-fabric";
const LIST_FIELDS = ["pills", "specialties"];

// Only fields that differ from the original are saved
const changesOnly = (values) => {
  const out = {};
  Object.keys(D).forEach((k) => {
    if (JSON.stringify(values[k]) !== JSON.stringify(D[k])) out[k] = values[k];
  });
  return out;
};
// Empty pills / list rows are left out when saving
const withoutBlankItems = (values) => {
  const out = { ...values };
  LIST_FIELDS.forEach((k) => { out[k] = values[k].map((x) => x.trim()).filter(Boolean); });
  return out;
};
const withDefaults = (saved) => {
  const out = { ...D };
  Object.entries(saved || {}).forEach(([k, v]) => {
    if (k in D && (Array.isArray(v) ? v.length : typeof v === "string" && v.trim())) out[k] = v;
  });
  return out;
};

const authHeaders = (json = true) => ({
  ...(json ? { "Content-Type": "application/json" } : {}),
  Authorization: `Bearer ${sessionStorage.getItem("adminToken")}`,
});

// Shows `text` with `highlight` in purple (same rule as the website)
const Highlighted = ({ text, highlight }) => {
  const at = highlight ? text.indexOf(highlight) : -1;
  if (at < 0) return text;
  return <>{text.slice(0, at)}<b className="af-hl">{highlight}</b>{text.slice(at + highlight.length)}</>;
};

// Which website part each section edits — used for the numbered headers and preview focus
const SECTIONS = [
  { id: "process", n: 1, title: "Process card", sub: "The white box on the left with the animation" },
  { id: "main",    n: 2, title: "Heading & text", sub: "Badge, big heading, two paragraphs and the small pills" },
  { id: "buttons", n: 3, title: "Buttons", sub: "The two buttons under the text" },
  { id: "details", n: 4, title: "Story & bespoke printing", sub: "The paragraph and box below the top part" },
  { id: "special", n: 5, title: "Our specialties", sub: "The ticked list" },
  { id: "bulk",    n: 6, title: "Bulk production", sub: "The minimum-order box" },
  { id: "closing", n: 7, title: "Closing lines", sub: "The last sentence and the italic tagline" },
];

const AboutFabricEditor = () => {
  const [values, setValues]     = useState(D);
  const [saved, setSaved]       = useState(D);
  const [isCustom, setIsCustom] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [open, setOpen]         = useState({ process: true, main: true });
  const [focus, setFocus]       = useState("main");
  const [confirm, setConfirm]   = useState(null);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${backend_url}/api/site-content/${KEY}`);
      const data = await res.json().catch(() => ({}));
      const v = withDefaults(data.success ? data.data : null);
      setValues(v); setSaved(v);
      setIsCustom(!!(data.success && data.data && Object.keys(data.data).length));
      setUpdatedAt(data.updatedAt || null);
      if (res.status === 404) toast.info("Showing the original text. Saving needs the latest backend to be deployed.");
    } catch {
      setValues(D); setSaved(D);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = JSON.stringify(values) !== JSON.stringify(saved);
  useEffect(() => {
    const onLeave = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const editedCount = useMemo(() => Object.keys(changesOnly(values)).length, [values]);

  // Problems that would show wrong on the website
  const warnings = useMemo(() => {
    const w = {};
    ["step1", "step2"].forEach((s) => {
      const text = values[`${s}Text`], hl = values[`${s}Highlight`];
      if (hl && !text.includes(hl)) w[`${s}Highlight`] = "These words aren't in the step text, so nothing will be purple. Copy them exactly (same capitals).";
    });
    Object.keys(D).forEach((k) => {
      const v = values[k];
      const empty = Array.isArray(v) ? !v.some((x) => x.trim()) : !String(v).trim();
      if (k !== "image" && empty) w[k] = "Empty — the original text will be shown instead.";
      else if (Array.isArray(v) && v.some((x) => !x.trim())) w[k] = "Empty ones are left out when you save.";
    });
    return w;
  }, [values]);

  const save = async () => {
    setSaving(true);
    try {
      const res  = await fetch(`${backend_url}/api/site-content/${KEY}`, {
        method: "PUT", headers: authHeaders(), body: JSON.stringify({ data: changesOnly(withoutBlankItems(values)) }),
      });
      if (res.status === 404) return { ok: false, message: "The server doesn't have this feature yet — deploy the latest backend, then save again." };
      const data = await res.json().catch(() => ({}));
      if (res.status === 401 || data.message === "Invalid Token") return { ok: false, message: "Your login has expired — please log in again, then save." };
      if (!data.success) return { ok: false, message: data.message || "Could not save" };
      const v = withDefaults(data.data);
      setValues(v); setSaved(v);
      setIsCustom(Object.keys(data.data || {}).length > 0);
      setUpdatedAt(data.updatedAt);
      return { ok: true };
    } catch {
      return { ok: false, message: "Could not reach the server — check your connection and try again." };
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image (GIF, JPG, PNG or WebP)"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res  = await fetch(`${backend_url}/api/site-content-image`, { method: "POST", headers: authHeaders(false), body: fd });
      if (res.status === 404) { toast.error("The server doesn't have this feature yet — deploy the latest backend first."); return; }
      const data = await res.json().catch(() => ({}));
      if (data.success) { set("image", data.url); toast.success("Image uploaded — save to put it on the website"); }
      else toast.error(data.message || "Upload failed");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const resetAll = () => setConfirm({
    title: "Go back to the original text?",
    text: "All your changes to this section are removed from the website.",
    yes: "Reset section",
    onYes: async () => {
      try {
        const res = await fetch(`${backend_url}/api/site-content/${KEY}`, { method: "DELETE", headers: authHeaders() });
        const data = await res.json().catch(() => ({}));
        if (data.success) { toast.success("Section reset to the original"); load(); }
        else toast.error(data.message || "Could not reset");
      } catch { toast.error("Could not reset"); }
    },
  });

  /* ── field helpers ── */
  const Edited = ({ k }) =>
    JSON.stringify(values[k]) !== JSON.stringify(D[k]) ? (
      <button type="button" className="af-reset-field" onClick={() => set(k, D[k])} title="Put back the original">
        Edited · Reset
      </button>
    ) : null;

  // "12/26" — turns amber near the limit and red at it
  const counter = (len, max) => (
    <span className={`af-counter ${len >= max ? "full" : len >= max * 0.9 ? "near" : ""}`}
      title={len >= max ? "Limit reached — keeps the design tidy" : `${max - len} characters left`}>
      {len}/{max}
    </span>
  );

  const Field = ({ k, label, hint, area, rows = 3 }) => (
    <div className="af-field">
      <div className="af-field-top">
        <label htmlFor={`af-${k}`}>{label}</label>
        <Edited k={k} />
      </div>
      {area ? (
        <textarea id={`af-${k}`} rows={rows} maxLength={L[k]} value={values[k]} onChange={(e) => set(k, e.target.value)} />
      ) : (
        <input id={`af-${k}`} maxLength={L[k]} value={values[k]} onChange={(e) => set(k, e.target.value)} />
      )}
      <div className="af-field-foot">
        {warnings[k] ? <p className="af-warn">⚠ {warnings[k]}</p> : hint ? <p className="af-hint">{hint}</p> : <span />}
        {counter(values[k].length, L[k])}
      </div>
    </div>
  );

  // Button text edited inside a button that looks like the website's
  const ButtonField = ({ k, label, look, hint }) => (
    <div className="af-btn-field">
      <div className="af-field-top">
        <label htmlFor={`af-${k}`}>{label}</label>
        <Edited k={k} />
      </div>
      <div className="af-btn-stage">
        <input id={`af-${k}`} className={`af-btn-input ${look}`} maxLength={L[k]} value={values[k]}
          style={{ width: `${Math.max(values[k].length, 8) + 5}ch` }}
          onChange={(e) => set(k, e.target.value)} placeholder={D[k]} />
      </div>
      <div className="af-field-foot">
        {warnings[k] ? <p className="af-warn">⚠ {warnings[k]}</p> : <p className="af-hint">{hint}</p>}
        {counter(values[k].length, L[k])}
      </div>
    </div>
  );

  const setItem = (k, i, text) => set(k, values[k].map((x, j) => (j === i ? text : x)));
  const removeItem = (k, i) => set(k, values[k].filter((_, j) => j !== i));
  const addItem = (k) => set(k, [...values[k], ""]);

  // Pills edited as the pills themselves, on the purple background
  const ChipField = ({ k, label, hint }) => {
    const lim = L[k];
    return (
      <div className="af-field">
        <div className="af-field-top">
          <label>{label} <span className="af-count">{values[k].length}/{lim.items}</span></label>
          <Edited k={k} />
        </div>
        <div className="af-chip-stage">
          {values[k].map((text, i) => (
            <span key={i} className={`af-chip ${!text.trim() ? "empty" : ""}`}>
              <input aria-label={`Pill ${i + 1}`} maxLength={lim.each} value={text} placeholder="Pill text"
                style={{ width: `${Math.max(text.length, 6) + 1}ch` }}
                onChange={(e) => setItem(k, i, e.target.value)} />
              <small>{text.length}/{lim.each}</small>
              <button type="button" aria-label={`Remove pill ${i + 1}`} onClick={() => removeItem(k, i)}>×</button>
            </span>
          ))}
          {values[k].length < lim.items && (
            <button type="button" className="af-chip-add" onClick={() => addItem(k)}>+ Add pill</button>
          )}
        </div>
        <div className="af-field-foot">
          {warnings[k] ? <p className="af-warn">⚠ {warnings[k]}</p> : <p className="af-hint">{hint} Up to {lim.items} pills, {lim.each} letters each.</p>}
        </div>
      </div>
    );
  };

  // Ticked list edited as rows with a ✓
  const ItemsField = ({ k, label }) => {
    const lim = L[k];
    return (
      <div className="af-field">
        <div className="af-field-top">
          <label>{label} <span className="af-count">{values[k].length}/{lim.items}</span></label>
          <Edited k={k} />
        </div>
        <ul className="af-items">
          {values[k].map((text, i) => (
            <li key={i}>
              <span className="af-tick">✓</span>
              <input aria-label={`Item ${i + 1}`} maxLength={lim.each} value={text} placeholder="Item text"
                onChange={(e) => setItem(k, i, e.target.value)} />
              {counter(text.length, lim.each)}
              <button type="button" aria-label={`Remove item ${i + 1}`} onClick={() => removeItem(k, i)}>×</button>
            </li>
          ))}
        </ul>
        {values[k].length < lim.items && (
          <button type="button" className="af-btn soft small-add" onClick={() => addItem(k)}>+ Add item</button>
        )}
        <div className="af-field-foot">
          {warnings[k] ? <p className="af-warn">⚠ {warnings[k]}</p> : <p className="af-hint">Up to {lim.items} items, {lim.each} letters each.</p>}
        </div>
      </div>
    );
  };

  const Section = ({ id, children }) => {
    const s = SECTIONS.find((x) => x.id === id);
    const isOpen = !!open[id];
    return (
      <section className={`af-section ${isOpen ? "open" : ""} ${focus === id ? "focused" : ""}`}>
        <button type="button" className="af-section-head" onClick={() => { setOpen((o) => ({ ...o, [id]: !o[id] })); setFocus(id); }}>
          <span className="af-num">{s.n}</span>
          <span className="af-section-text">
            <span className="af-section-title">{s.title}</span>
            <span className="af-section-sub">{s.sub}</span>
          </span>
          <span className="af-chevron">{isOpen ? "−" : "+"}</span>
        </button>
        {isOpen && <div className="af-section-body" onFocusCapture={() => setFocus(id)}>{children}</div>}
      </section>
    );
  };

  if (loading) return <div className="af-page"><p className="af-muted">Loading…</p></div>;

  const v = values;
  return (
    <div className="af-page">
      <div className="af-header">
        <div>
          <h1>About Our Fabric</h1>
          <p>Change the words and picture of the purple “About Our Fabric” section on the home page. The design stays the same.</p>
        </div>
        <div className="af-header-right">
          <span className={`af-status ${isCustom ? "custom" : ""}`}>
            {isCustom ? `● ${editedCount} field${editedCount === 1 ? "" : "s"} changed${updatedAt ? ` · saved ${new Date(updatedAt).toLocaleDateString()}` : ""}` : "● Original text"}
          </span>
          {isCustom && <button className="af-btn ghost" onClick={resetAll}>Reset to original</button>}
        </div>
      </div>

      <div className="af-layout">
        {/* ── Form ── */}
        <div className="af-form">
          {Section({ id: "process", children: (
            <>
              {Field({ k: "processTitle", label: "Card title", hint: "Small heading at the top of the white card" })}
              <div className="af-pair">
                {Field({ k: "step1Text", label: "Step 1 text" })}
                {Field({ k: "step1Highlight", label: "Purple words in step 1", hint: "Must be copied exactly from the step text" })}
              </div>
              <div className="af-field">
                <div className="af-field-top"><label>Picture / animation</label><Edited k="image" /></div>
                <div className="af-image">
                  <img src={v.image || aboutGif} alt="Current" />
                  <div className="af-image-actions">
                    <p className="af-hint">{v.image ? "Your uploaded picture" : "The original animation"}</p>
                    <input ref={fileRef} type="file" accept="image/gif,image/jpeg,image/png,image/webp" hidden
                      onChange={(e) => uploadImage(e.target.files[0])} />
                    <button type="button" className="af-btn soft" disabled={uploading} onClick={() => fileRef.current?.click()}>
                      {uploading ? "Uploading…" : "Upload new picture"}
                    </button>
                    {v.image && <button type="button" className="af-link" onClick={() => set("image", "")}>Use the original animation</button>}
                    <p className="af-hint">GIF, JPG, PNG or WebP, up to 10 MB. Shown 280 × 240, cropped to fit.</p>
                  </div>
                </div>
              </div>
              <div className="af-pair">
                {Field({ k: "step2Text", label: "Step 2 text" })}
                {Field({ k: "step2Highlight", label: "Purple words in step 2", hint: "Must be copied exactly from the step text" })}
              </div>
            </>
          ) })}

          {Section({ id: "main", children: (
            <>
              <div className="af-pair">
                {Field({ k: "badge", label: "Badge", hint: "Shown in capital letters" })}
                {Field({ k: "heading", label: "Big heading" })}
              </div>
              {Field({ k: "intro1", label: "First paragraph", area: true, rows: 3 })}
              {Field({ k: "intro2", label: "Second paragraph", area: true, rows: 4 })}
              {ChipField({ k: "pills", label: "Pills", hint: "Click a pill to change its words." })}
            </>
          ) })}

          {Section({ id: "buttons", children: (
            <>
              {ButtonField({ k: "button1Text", label: "White button", look: "solid", hint: "Click the button to change its words. Opens the Explore Our Ideas page." })}
              {ButtonField({ k: "button2Text", label: "Outline button", look: "outline", hint: "Click the button to change its words. Opens WhatsApp." })}
              {Field({ k: "whatsappMessage", label: "WhatsApp message", hint: "Typed in for the customer when they tap the outline button" })}
            </>
          ) })}

          {Section({ id: "details", children: (
            <>
              {Field({ k: "story", label: "Story paragraph", area: true, rows: 4 })}
              {Field({ k: "bespokeTitle", label: "Box heading" })}
              {Field({ k: "bespoke1", label: "Box paragraph 1", area: true, rows: 4 })}
              {Field({ k: "bespoke2", label: "Box paragraph 2", area: true, rows: 3 })}
            </>
          ) })}

          {Section({ id: "special", children: (
            <>
              {Field({ k: "specialtiesTitle", label: "Heading" })}
              {ItemsField({ k: "specialties", label: "Ticked items" })}
            </>
          ) })}

          {Section({ id: "bulk", children: (
            <>
              {Field({ k: "bulkTitle", label: "Heading" })}
              {Field({ k: "bulkText", label: "Text", area: true, rows: 2 })}
              <div className="af-triple">
                {Field({ k: "minLabel", label: "Small label" })}
                {Field({ k: "minValue", label: "Big value" })}
                {Field({ k: "minUnit", label: "Under the value" })}
              </div>
            </>
          ) })}

          {Section({ id: "closing", children: (
            <>
              {Field({ k: "closing", label: "Closing sentence", area: true, rows: 2 })}
              {Field({ k: "tagline", label: "Italic tagline" })}
            </>
          ) })}
        </div>

        {/* ── Live preview (same look as the website, smaller) ── */}
        <aside className="af-preview-wrap">
          <div className="af-preview-label">Live preview <span>updates as you type</span></div>
          <div className="af-preview">
            <div className={`af-pv-card ${focus === "process" ? "glow" : ""}`}>
              <div className="af-pv-card-head"><i /><i /><i /> {v.processTitle || D.processTitle}</div>
              <div className="af-pv-steps">
                <span className="af-pv-step"><em>1</em><Highlighted text={v.step1Text || D.step1Text} highlight={v.step1Highlight} /></span>
                <img src={v.image || aboutGif} alt="" />
                <span className="af-pv-step"><em>2</em><Highlighted text={v.step2Text || D.step2Text} highlight={v.step2Highlight} /></span>
              </div>
            </div>
            <div className={`af-pv-main ${focus === "main" || focus === "buttons" ? "glow" : ""}`}>
              <span className="af-pv-badge">{v.badge || D.badge}</span>
              <h2>{v.heading || D.heading}</h2>
              <p className="p1">{v.intro1 || D.intro1}</p>
              <p className="p2">{v.intro2 || D.intro2}</p>
              <div className="af-pv-pills">{(v.pills.length ? v.pills : D.pills).filter(Boolean).map((p, i) => <span key={i}>{p}</span>)}</div>
              <div className={`af-pv-buttons ${focus === "buttons" ? "glow" : ""}`}>
                <span className="solid">{v.button1Text || D.button1Text}</span>
                <span className="outline">{v.button2Text || D.button2Text}</span>
              </div>
            </div>
            <div className={`af-pv-lower ${["details", "special", "bulk", "closing"].includes(focus) ? "glow" : ""}`}>
              <p className="af-pv-story">{v.story || D.story}</p>
              <div className="af-pv-boxes">
                <div><b>{v.bespokeTitle || D.bespokeTitle}</b><p>{v.bespoke1 || D.bespoke1}</p></div>
                <div><b>{v.specialtiesTitle || D.specialtiesTitle}</b>
                  <ul>{(v.specialties.length ? v.specialties : D.specialties).filter(Boolean).map((s, i) => <li key={i}>✓ {s}</li>)}</ul>
                </div>
              </div>
              <div className="af-pv-bulk">
                <div><b>{v.bulkTitle || D.bulkTitle}</b><p>{v.bulkText || D.bulkText}</p></div>
                <div className="af-pv-min"><small>{v.minLabel || D.minLabel}</small><strong>{v.minValue || D.minValue}</strong><small>{v.minUnit || D.minUnit}</small></div>
              </div>
              <p className="af-pv-closing">{v.closing || D.closing}</p>
              <p className="af-pv-tagline">{v.tagline || D.tagline}</p>
            </div>
          </div>
        </aside>
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={save} what="the About Our Fabric section"
        onDiscard={() => setConfirm({ title: "Discard your changes?", text: "The text goes back to how it was last saved.", yes: "Discard", onYes: () => setValues(saved) })} />

      {confirm && (
        <div className="af-modal-overlay" onClick={() => setConfirm(null)}>
          <div className="af-modal" role="dialog" aria-label={confirm.title} onClick={(e) => e.stopPropagation()}>
            <h3>{confirm.title}</h3>
            <p>{confirm.text}</p>
            <div className="af-modal-actions">
              <button className="af-btn ghost" onClick={() => setConfirm(null)}>Cancel</button>
              <button className="af-btn danger" onClick={() => { confirm.onYes(); setConfirm(null); }}>{confirm.yes}</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export { changesOnly, withDefaults, LIST_FIELDS };
export default AboutFabricEditor;
